import React, { useState, useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { useWebviewMode } from '../../hooks/useWebviewMode';
import axios from 'axios';
import ReactSelect from 'react-select';
import { useUser } from '@clerk/clerk-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Upload,
  Trash2,
  X,
  Image as ImageIcon,
  Search,
  Building2,
  CheckCircle2,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Wand2,
  RotateCw,
  Calendar
} from 'lucide-react';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { showLoading, hideLoading } from '../../store/slices/loadingState';
import { clientsSelector } from '../../store/slices/clients';

import {
  API_KEY,
  API_CLIENTS_NAME,
  DELETE_CL_IMAGE,
  ROTATE_IMAGE,
  DELETE_IMAGES,
  LIST_CL_IMAGES_GOOGLE_BUSINESS,
  LIST_ALL_CLIENT_IMAGES,
  LOAD_IMAGES_API,
  CREATE_ALTERNATIVE_IMAGE,
  LIST_ALTERNATED_IMAGES,
  UPDATE_STOCK_CLIENT_UPLOAD_IMAGES,
  BUCKET_CS_IMAGES_UPLOAD,
  S3_GET_PRESIGNED_URLS
} from '../../utils/constants';
import { authSelector } from '../../store/slices/auth';
import LoadingSpinner from '../../pages/Calendar/GallerySection/StockImagesComponents/Partials/LoadingSpinner';
import Swal from 'sweetalert2';
import { convertHeicToJpeg } from './convertHeicToJpeg';

const Images = () => {
  const dispatch = useDispatch();
  const { user } = useUser();

  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);
  const isWebviewMode = useWebviewMode();
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  // Use the selected client's ID
  const clientId = selectedClient?.id || null;

  const webviewParam =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('webview')
      : null;
  const isWebView = ['1', 'true', 'yes', 'on'].includes(
    String(webviewParam || '').toLowerCase()
  );

  const [selectedImages, setSelectedImages] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Combined loading state - true when either initial load or filtering
  const isLoadingData = loading || initialLoading;

  const [imageSource, setImageSource] = useState('gbp');
  const [imageStats, setImageStats] = useState({
    gbp: 0,
    imageToImage: 0
  });

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteErrorModalOpen, setDeleteErrorModalOpen] = useState(false);
  const [deleteMultipleConfirmModalOpen, setDeleteMultipleConfirmModalOpen] =
    useState(false);
  const [deleteMultipleErrorModalOpen, setDeleteMultipleErrorModalOpen] =
    useState(false);
  const [files, setFiles] = useState({ length: 0 });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadErrorModalOpen, setUploadErrorModalOpen] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] =
    useState('Upload failed.');
  const uploadInputRef = useRef(null);
  const clientIdRef = useRef(clientId);
  const imageSourceRef = useRef('gbp');
  const galleryRefreshTimersRef = useRef([]);
  const galleryRequestSeqRef = useRef(0);
  clientIdRef.current = clientId;
  const [resyncLoading, setResyncLoading] = useState(false);
  const [rotatingImageUrl, setRotatingImageUrl] = useState(null);
  const [imageVersions, setImageVersions] = useState({});
  const [galleryRefreshing, setGalleryRefreshing] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateSubmitting, setGenerateSubmitting] = useState(false);
  const [generateAppImages, setGenerateAppImages] = useState([]);
  const [generateSelectedImages, setGenerateSelectedImages] = useState([]);
  const [generateResults, setGenerateResults] = useState(null);
  const [generateResultsView, setGenerateResultsView] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    () => window.innerWidth < 640
  );
  const [clientNavigationMode, setClientNavigationMode] = useState('all');

  const hasNoAIFlag = (customer) => {
    const flags = customer?.clientFlags;
    if (Array.isArray(flags)) {
      return flags.includes('NO_AI');
    }
    if (typeof flags === 'string') {
      return flags
        .split(',')
        .map((flag) => flag.trim())
        .includes('NO_AI');
    }
    return false;
  };

  const noAIClientList = useMemo(
    () => customerList.filter((customer) => hasNoAIFlag(customer)),
    [customerList]
  );

  const navigableCustomerList =
    clientNavigationMode === 'no_ai' ? noAIClientList : customerList;

  const searchableCustomerList = useMemo(
    () =>
      navigableCustomerList.map((customer) => ({
        ...customer,
        label: customer.clientName,
        value: customer.clientName
      })),
    [navigableCustomerList]
  );
  const sourceLabels = {
    gbp: {
      desktop: 'Client Images',
      mobile: 'Client',
      title: 'Client Images',
      mobileTitle: 'Client Images'
    },
    'image-to-image': {
      desktop: 'Image-to-Image',
      mobile: 'Img2Img',
      title: 'Image-to-Image',
      mobileTitle: 'Image-to-Image'
    }
  };
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 640);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (clientNavigationMode !== 'no_ai') return;
    if (!selectedClient?.id) return;
    if (hasNoAIFlag(selectedClient)) return;
    if (!noAIClientList.length) return;

    handleChangeClient(noAIClientList[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientNavigationMode, selectedClient?.id, noAIClientList]);

  // Handle change in client search select
  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const encodedClientName = encodeURIComponent(selectedOption.clientName);
      const response = await axios.get(
        `${API_CLIENTS_NAME}${encodedClientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including socialnetworks
        dispatch(setSelectedClient(response.data));
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(setSelectedClient(selectedOption));
    }
  };

  const galleryGet = (url) => {
    const bypassWebviewCache = isWebView || isWebviewMode;
    if (!bypassWebviewCache) return axios.get(url);

    const separator = url.includes('?') ? '&' : '?';
    return axios.get(`${url}${separator}_=${Date.now()}`);
  };

  const getImageUrl = (image) => image?.sourceUrl || image?.SK || '';

  const galleryImageSrc = (url) => {
    if (!url) return '';
    const version = imageVersions[url];
    if (!version) return url;

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_=${version}`;
  };

  const fetchImages = async (source, { silent = false } = {}) => {
    const requestSeq = ++galleryRequestSeqRef.current;
    const isCurrentRequest = () => requestSeq === galleryRequestSeqRef.current;

    if (!silent) {
      dispatch(showLoading());
      setLoading(true);
      setSelectedImages([]);
    }

    if (!clientId) {
      if (!silent) {
        dispatch(hideLoading());
        setLoading(false);
        setInitialLoading(false);
      }
      return;
    }

    try {
      let fetchedImages = [];

      if (source === 'image-to-image') {
        // Fetch AI-generated alternative images from the database
        const response = await galleryGet(
          `${LIST_ALTERNATED_IMAGES}/${clientId}`
        );
        if (response.data && response.data.length) {
          fetchedImages = response.data.map((img) => ({
            SK: img.SK,
            sourceUrl: img.SK,
            source: 'gemini_alt',
            keywords: img.keywords,
            orderNewImage: img.orderNewImage
          }));
        }
      } else {
        // gbp - fetch all client images (gmb, manual_upload, app)
        const response = await galleryGet(
          `${LIST_ALL_CLIENT_IMAGES}/${clientId}`
        );
        fetchedImages = response.data || [];
      }

      // Sort images by date (most recent first)
      const sortedImages = fetchedImages.sort((a, b) => {
        // Para Client Images: lastUse (segundos) -> convertir a milisegundos
        // Para App Images: LastModified (ISO string)
        // Para Stock Images: createdAt
        const dateA = a.lastUse
          ? a.lastUse * 1000
          : a.LastModified || a.createdAt || 0;
        const dateB = b.lastUse
          ? b.lastUse * 1000
          : b.LastModified || b.createdAt || 0;

        // Convert to timestamp if they're strings
        const timeA =
          typeof dateA === 'string' ? new Date(dateA).getTime() : dateA || 0;
        const timeB =
          typeof dateB === 'string' ? new Date(dateB).getTime() : dateB || 0;

        // Sort descending (newest first)
        return timeB - timeA;
      });

      if (!isCurrentRequest()) return null;

      setImages(sortedImages);

      // Immediately reflect the count for the active source, then fetch the other
      const activeKey = source === 'image-to-image' ? 'imageToImage' : 'gbp';
      setImageStats((prev) => ({ ...prev, [activeKey]: sortedImages.length }));

      try {
        if (source === 'image-to-image') {
          const gbpRes = await galleryGet(
            `${LIST_ALL_CLIENT_IMAGES}/${clientId}`
          );
          setImageStats((prev) => ({ ...prev, gbp: gbpRes.data?.length || 0 }));
        } else {
          const altRes = await galleryGet(
            `${LIST_ALTERNATED_IMAGES}/${clientId}`
          );
          setImageStats((prev) => ({
            ...prev,
            imageToImage: altRes.data?.length || 0
          }));
        }
      } catch (statsError) {
        console.error('Error fetching image stats:', statsError);
      }

      return sortedImages.length;
    } catch (error) {
      console.error('Error fetching images:', error);
      if (!silent && isCurrentRequest()) {
        setImages([]);
        setImageStats({ gbp: 0, imageToImage: 0 });
      }
      return null;
    } finally {
      if (!silent && isCurrentRequest()) {
        dispatch(hideLoading());
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const scheduleWebviewGalleryRefresh = (source) => {
    if (!isWebView && !isWebviewMode) return;

    const uploadedForClient = clientId;
    const baselineCount = images.length;
    const refreshGaps = [3000, 5000, 7000, 10000];

    const wait = (ms) =>
      new Promise((resolve) => {
        const timerId = window.setTimeout(resolve, ms);
        galleryRefreshTimersRef.current.push(timerId);
      });

    const run = async () => {
      for (const gap of refreshGaps) {
        await wait(gap);
        if (clientIdRef.current !== uploadedForClient) return;
        if (imageSourceRef.current !== source) return;

        const count = await fetchImages(source, { silent: true });
        if (typeof count === 'number' && count > baselineCount) return;
      }
    };

    run();
  };

  const handleRefreshGallery = async () => {
    setGalleryRefreshing(true);
    try {
      await fetchImages(imageSource, { silent: true });
    } finally {
      setGalleryRefreshing(false);
    }
  };

  const fetchImagesForGenerate = async () => {
    if (!clientId) {
      setGenerateAppImages([]);
      setGenerateSelectedImages([]);
      return;
    }

    setGenerateLoading(true);

    try {
      const [clientImagesResponse, alternativeImagesResponse] =
        await Promise.all([
          galleryGet(`${LIST_ALL_CLIENT_IMAGES}/${clientId}`),
          galleryGet(`${LIST_ALTERNATED_IMAGES}/${clientId}`)
        ]);

      const clientImages = clientImagesResponse?.data || [];
      const alternativeImages = alternativeImagesResponse?.data || [];

      const formattedImages = [
        ...clientImages.map((img) => ({
          ...img,
          sourceUrl: img.sourceUrl || img.SK,
          source: img.source || 'client'
        })),
        ...alternativeImages.map((img) => ({
          ...img,
          sourceUrl: img.sourceUrl || img.SK,
          source: 'gemini_alt'
        }))
      ]
        .filter((img) => img.sourceUrl)
        .filter(
          (img, index, allImages) =>
            allImages.findIndex(
              (candidate) => candidate.sourceUrl === img.sourceUrl
            ) === index
        )
        .sort((a, b) => {
          const dateA = a.lastUse
            ? a.lastUse * 1000
            : a.LastModified || a.createdAt || 0;
          const dateB = b.lastUse
            ? b.lastUse * 1000
            : b.LastModified || b.createdAt || 0;
          const timeA =
            typeof dateA === 'string' ? new Date(dateA).getTime() : dateA;
          const timeB =
            typeof dateB === 'string' ? new Date(dateB).getTime() : dateB;
          return timeB - timeA;
        });

      setGenerateAppImages(formattedImages);
      setGenerateSelectedImages((prevSelected) =>
        prevSelected.filter((url) =>
          formattedImages.some((img) => (img.sourceUrl || img.SK) === url)
        )
      );
    } catch (error) {
      console.error('Error fetching app images for generation:', error);
      setGenerateAppImages([]);
      setGenerateSelectedImages([]);
    } finally {
      setGenerateLoading(false);
    }
  };

  const openGenerateModal = async () => {
    setGenerateSelectedImages([]);
    setGenerateResults(null);
    setGenerateResultsView(false);
    setGenerateModalOpen(true);
    await fetchImagesForGenerate();
  };

  const toggleGenerateImageSelection = (imageUrl) => {
    setGenerateSelectedImages((prev) =>
      prev.includes(imageUrl)
        ? prev.filter((url) => url !== imageUrl)
        : [...prev, imageUrl]
    );
  };

  const handleToggleSelectAllGenerate = () => {
    const allUrls = generateAppImages
      .map((img) => img.sourceUrl || img.SK)
      .filter(Boolean);

    setGenerateSelectedImages((prev) =>
      prev.length === allUrls.length ? [] : allUrls
    );
  };

  const selectedGeneratePayload = useMemo(
    () =>
      generateAppImages
        .filter((img) =>
          generateSelectedImages.includes(img.sourceUrl || img.SK)
        )
        .map((img) => ({
          sourceUrl: img.sourceUrl || img.SK,
          source: 'app',
          lastModified: img.LastModified || null
        })),
    [generateAppImages, generateSelectedImages]
  );

  const groupResultsByOriginalUrl = (images) =>
    images.reduce((acc, item) => {
      if (!acc[item.originalUrl]) acc[item.originalUrl] = [];
      acc[item.originalUrl].push(item);
      return acc;
    }, {});

  const handleGenerateFromSelected = async () => {
    if (!selectedGeneratePayload.length) return;
    if (!clientId) return;

    setGenerateSubmitting(true);

    try {
      const imageUrls = selectedGeneratePayload.map((img) => img.sourceUrl);

      const response = await axios.post(CREATE_ALTERNATIVE_IMAGE, {
        clientId,
        imageUrls
      });

      const data = response.data;
      const grouped = groupResultsByOriginalUrl(data.images || []);

      setGenerateResults({ ...data, grouped });
      setGenerateResultsView(true);

      // Refresh the image-to-image gallery with the newly saved images
      if (imageSource === 'image-to-image') {
        await fetchImages('image-to-image');
      }
    } catch (error) {
      console.error('Error generating alternative images:', error);
      Swal.fire({
        icon: 'error',
        title: 'Generation Failed',
        text:
          error?.response?.data?.message ||
          'Could not generate image alternatives. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setGenerateSubmitting(false);
    }
  };

  const handleImageSourceChange = (source) => {
    imageSourceRef.current = source;
    setImageSource(source);
    fetchImages(source);
  };

  useEffect(() => {
    imageSourceRef.current = imageSource;
  }, [imageSource]);

  useEffect(() => {
    const timers = galleryRefreshTimersRef;
    return () => {
      timers.current.forEach((timerId) => window.clearTimeout(timerId));
      timers.current = [];
    };
  }, []);

  useEffect(() => {
    fetchImages('gbp');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleDeleteImage = (image) => {
    setImageToDelete(image);
    setDeleteConfirmModalOpen(true);
  };

  const handleRotateImage = async (image) => {
    const sourceUrl = getImageUrl(image);
    if (!sourceUrl || rotatingImageUrl) return;

    if (!ROTATE_IMAGE) {
      Swal.fire({
        icon: 'error',
        title: 'Rotate unavailable',
        text: 'The rotate image endpoint is not configured.',
        confirmButtonText: 'OK'
      });
      return;
    }

    setRotatingImageUrl(sourceUrl);

    try {
      await axios.post(
        ROTATE_IMAGE,
        { sourceUrl, degrees: 90 },
        { headers: { 'Content-Type': 'application/json' } }
      );

      setImageVersions((previous) => ({
        ...previous,
        [sourceUrl]: Date.now()
      }));
    } catch (error) {
      console.error('Error rotating image:', error);
      const responseMessage =
        error?.response?.data?.message || error?.response?.data?.error;
      Swal.fire({
        icon: 'error',
        title: 'Rotate failed',
        text:
          typeof responseMessage === 'string'
            ? responseMessage
            : 'Could not rotate the image. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setRotatingImageUrl(null);
    }
  };

  const getAppImageDeletePayload = (image) => {
    if (!DELETE_IMAGES) {
      throw new Error(
        'REACT_APP_DELETE_IMAGES is not configured. Add the image deletion endpoint to the local environment.'
      );
    }

    const imageUrl = image.sourceUrl || image.SK;
    if (!imageUrl) {
      throw new Error('The image does not have a source URL.');
    }

    const parsedUrl = imageUrl.includes('://') ? new URL(imageUrl) : null;
    const path = parsedUrl
      ? parsedUrl.pathname.replace(/^\/+/, '')
      : imageUrl.replace(/^\/+/, '').split('?')[0];
    let pathParts = path.split('/').filter(Boolean);

    // Regional S3 URLs include the bucket as the first path segment:
    // s3.region.amazonaws.com/bucket/clientId/thumbnails/file.jpg
    if (parsedUrl?.hostname.startsWith('s3.') && pathParts.length > 1) {
      pathParts = pathParts.slice(1);
    }

    const userId = pathParts.shift();
    const filename = pathParts[pathParts.length - 1];
    const thumbnailsIndex = pathParts.indexOf('thumbnails');
    const thumbnailPath =
      thumbnailsIndex >= 0
        ? pathParts.slice(thumbnailsIndex).join('/')
        : `thumbnails/${filename}`;

    if (!userId || !filename) {
      throw new Error(`Could not determine the S3 path for image: ${imageUrl}`);
    }

    return {
      user_id: `${userId}/`,
      path_thumbnail: thumbnailPath,
      path_image: filename
    };
  };

  const logDeleteResponse = (image, payload, response) => {
    const responseData = response?.data;
    const responseBody = responseData?.body || responseData;
    const errors = responseBody?.Errors || [];

    console.groupCollapsed('[Images] Delete response');
    console.log('Image:', image);
    console.log('Payload:', payload);
    console.log('HTTP status:', response?.status);
    console.log('Response:', responseData);
    console.log('Deleted:', responseBody?.Deleted || []);
    console.log('Errors:', errors);
    console.groupEnd();

    if (errors.length > 0) {
      throw new Error(`Image deletion returned ${errors.length} S3 error(s).`);
    }
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;

    try {
      const isAppImage = imageToDelete.PK?.startsWith('app#');

      if (isAppImage) {
        const payload = getAppImageDeletePayload(imageToDelete);
        const response = await axios.delete(DELETE_IMAGES, { data: payload });
        logDeleteResponse(imageToDelete, payload, response);
      } else {
        const payloadClientId =
          imageToDelete.PK ||
          (imageToDelete.source === 'gemini_alt'
            ? `alternated#${clientId}`
            : clientId);

        await axios({
          method: 'post',
          url: DELETE_CL_IMAGE,
          headers: { 'Content-Type': 'application/json' },
          data: JSON.stringify({
            clientId: payloadClientId,
            sourceUrl: imageToDelete.sourceUrl || imageToDelete.SK
          })
        });
      }

      const imageUrl = imageToDelete.sourceUrl || imageToDelete.SK;

      setImages((prev) =>
        prev.filter((img) => {
          const currentUrl = img.sourceUrl || img.SK;
          return currentUrl !== imageUrl;
        })
      );

      setSelectedImages((prevSelected) => {
        const imgIndex = images.findIndex((img) => {
          const currentUrl = img.sourceUrl || img.SK;
          return currentUrl === imageUrl;
        });
        return prevSelected.filter((idx) => idx !== imgIndex);
      });

      setDeleteConfirmModalOpen(false);
      setImageToDelete(null);
      await fetchImages(imageSource);
    } catch (error) {
      console.error('Error deleting image:', error);
      setDeleteConfirmModalOpen(false);
      setImageToDelete(null);
      setDeleteErrorModalOpen(true);
    }
  };

  const handleDeleteSelected = () => {
    setDeleteMultipleConfirmModalOpen(true);
  };

  const confirmDeleteMultiple = async () => {
    try {
      const deletePromises = selectedImages.map((idx) => {
        const imageToDelete = images[idx];
        const isAppImage = imageToDelete.PK?.startsWith('app#');

        if (isAppImage) {
          const payload = getAppImageDeletePayload(imageToDelete);
          return axios
            .delete(DELETE_IMAGES, { data: payload })
            .then((response) => {
              logDeleteResponse(imageToDelete, payload, response);
              return response;
            });
        }

        const payloadClientId =
          imageToDelete.PK ||
          (imageToDelete.source === 'gemini_alt'
            ? `alternated#${clientId}`
            : clientId);

        return axios({
          method: 'post',
          url: DELETE_CL_IMAGE,
          headers: { 'Content-Type': 'application/json' },
          data: JSON.stringify({
            clientId: payloadClientId,
            sourceUrl: imageToDelete.sourceUrl || imageToDelete.SK
          })
        });
      });

      await Promise.all(deletePromises);

      // Remove deleted images from local state (selectedImages contains indices)
      setImages((prev) =>
        prev.filter((_, idx) => !selectedImages.includes(idx))
      );

      setSelectedImages([]);
      setDeleteMultipleConfirmModalOpen(false);
      await fetchImages(imageSource);
    } catch (error) {
      console.error('Error deleting images:', error);
      setDeleteMultipleConfirmModalOpen(false);
      setDeleteMultipleErrorModalOpen(true);
    }
  };

  const handleUploadFiles = async () => {
    setUploadingImages(true);
    setProgress(0);
    setUploadErrorMessage('Upload failed.');
    const uploadedImages = [];

    try {
      const uploadFiles = [];
      for (let i = 0; i < files.length; i += 1) {
        uploadFiles.push(await convertHeicToJpeg(files[i]));
      }

      const uploadViaPresignedLibrary = async (filesToSend) => {
        for (let i = 0; i < filesToSend.length; i++) {
          const file = filesToSend[i];
          const fileType = file.type;

          const sanitizedFileName = file.name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9.-]/g, '_')
            .replace(/\s+/g, '_');

          const fileName = `${uuid()}-${sanitizedFileName}`;

          const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              bucketName: BUCKET_CS_IMAGES_UPLOAD,
              files: [{ fileName, fileType }]
            })
          });

          if (!presignedResponse.ok)
            throw new Error('Failed to get presigned URL');

          const { urls } = await presignedResponse.json();
          const presignedUrl = urls?.[0]?.url;

          if (!presignedUrl) throw new Error('No presigned URL returned');

          const uploadResult = await fetch(presignedUrl, {
            method: 'PUT',
            headers: { 'Content-Type': fileType },
            body: file
          });

          if (!uploadResult.ok) throw new Error('File upload to S3 failed');

          const uploadedFileUrl = `https://${BUCKET_CS_IMAGES_UPLOAD}.s3.amazonaws.com/${fileName}`;

          const currentTimestampSeconds = Math.floor(Date.now() / 1000);

          await axios.post(
            UPDATE_STOCK_CLIENT_UPLOAD_IMAGES,
            {
              clientId,
              sourceUrl: uploadedFileUrl,
              source: 'manual_upload',
              keywords: '',
              lastUse: currentTimestampSeconds
            },
            {
              headers: {
                'X-API-KEY': API_KEY,
                'Content-Type': 'application/json'
              }
            }
          );

          uploadedImages.push({
            SK: uploadedFileUrl,
            sourceUrl: uploadedFileUrl,
            source: 'manual_upload',
            lastUse: currentTimestampSeconds
          });

          setProgress(Math.round(((i + 1) / filesToSend.length) * 100));
        }
      };

      if (isWebView) {
        if (!clientId) {
          throw new Error('Missing client id for webview upload');
        }

        let bridgeReady = false;
        for (let attempt = 0; attempt < 50; attempt++) {
          if (window?.ReactNativeWebView?.postMessage) {
            bridgeReady = true;
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Browser webview has no native bridge. The app-bucket upload fails
        // with "Failed to fetch", so use the same library upload as the web app.
        if (!bridgeReady || !window?.ReactNativeWebView?.postMessage) {
          await uploadViaPresignedLibrary(uploadFiles);

          setUploadingImages(false);
          setShowUploadModal(false);
          setFiles({ length: 0 });
          setProgress(0);
          await fetchImages(imageSource);
          scheduleWebviewGalleryRefresh(imageSource);
          return;
        }

        const requestId = `wv-upload-${Date.now()}-${uuid()}`;

        const serializedFiles = await Promise.all(
          uploadFiles.map(
            (file) =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () =>
                  resolve({
                    name: file.name,
                    type: file.type,
                    dataUrl: reader.result,
                    description: ''
                  });
                reader.onerror = () =>
                  reject(new Error(`Could not read file ${file.name}`));
                reader.readAsDataURL(file);
              })
          )
        );

        setProgress(25);

        await new Promise((resolve, reject) => {
          let resolved = false;

          const cleanup = () => {
            window.removeEventListener('message', handleBridgeMessage);
            document.removeEventListener('message', handleBridgeMessage);
            clearTimeout(timeoutId);
          };

          const handleBridgeMessage = (event) => {
            if (!event?.data) return;

            let payload = event.data;
            if (typeof payload === 'string') {
              try {
                payload = JSON.parse(payload);
              } catch (error) {
                return;
              }
            }

            if (
              payload?.type !== 'WEBVIEW_UPLOAD_IMAGES_RESULT' ||
              payload?.requestId !== requestId
            ) {
              return;
            }

            if (resolved) return;
            resolved = true;
            cleanup();

            if (payload?.success) {
              resolve(payload);
            } else {
              reject(
                new Error(payload?.error || 'Native webview upload failed')
              );
            }
          };

          const timeoutId = setTimeout(() => {
            if (resolved) return;
            resolved = true;
            cleanup();
            reject(new Error('Timed out waiting for native webview upload'));
          }, 180000);

          window.addEventListener('message', handleBridgeMessage);
          document.addEventListener('message', handleBridgeMessage);

          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'WEBVIEW_UPLOAD_IMAGES',
              requestId,
              payload: {
                clientId,
                files: serializedFiles
              }
            })
          );
        });

        setProgress(100);

        setUploadingImages(false);
        setShowUploadModal(false);
        setFiles({ length: 0 });
        setProgress(0);
        await fetchImages(imageSource);
        scheduleWebviewGalleryRefresh(imageSource);
        return;
      }

      await uploadViaPresignedLibrary(uploadFiles);

      setUploadingImages(false);
      setShowUploadModal(false);
      setFiles({ length: 0 });
      setProgress(0);
      await fetchImages(imageSource);
    } catch (error) {
      console.error('Error during upload:', error);
      setUploadErrorMessage(
        String(error?.message || error || 'Upload failed.')
      );
      setUploadErrorModalOpen(true);
      setUploadingImages(false);
    }
  };

  const renderUploadPreview = () => {
    const itemsList = [];
    for (let i = 0; i < files.length; i++) itemsList.push(files[i]);

    return itemsList.map((item, index) => {
      const fileSize =
        item.size < 1024
          ? `${item.size} bytes`
          : item.size < 1048576
          ? `${(item.size / 1024).toFixed(1)} KB`
          : `${(item.size / 1048576).toFixed(1)} MB`;

      const fileName =
        item.name.length > 35 ? `${item.name.substring(0, 35)}...` : item.name;

      return (
        <div
          key={index}
          className="flex items-center justify-between p-4 mb-2"
          style={{
            backgroundColor: '#f9fafb',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb'
          }}>
          <div className="flex-1 min-w-0 mr-4">
            <p
              className="text-sm font-medium truncate"
              style={{ color: '#1f2937' }}>
              {fileName}
            </p>
            <p
              className="text-xs"
              style={{ color: '#6b7280', marginTop: '0.25rem' }}>
              {fileSize}
            </p>
          </div>
          <img
            src={URL.createObjectURL(item)}
            alt={`Preview ${index + 1}`}
            className="w-24 h-24 object-cover"
            style={{
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb'
            }}
          />
        </div>
      );
    });
  };

  const getSourceLabel = () => {
    switch (imageSource) {
      case 'image-to-image':
        return 'Image-to-Image';
      case 'gbp':
      default:
        return 'Client Images';
    }
  };

  const formatImageDateLabel = (image) => {
    const rawDate =
      image?.createdAt ?? image?.LastModified ?? image?.lastUse ?? null;

    if (!rawDate) return 'No date';

    let timestamp = rawDate;

    if (typeof timestamp === 'number') {
      timestamp = timestamp < 100000000000 ? timestamp * 1000 : timestamp;
    } else if (typeof timestamp === 'string') {
      const maybeNumber = Number(timestamp);
      if (!Number.isNaN(maybeNumber)) {
        timestamp =
          maybeNumber < 100000000000 ? maybeNumber * 1000 : maybeNumber;
      }
    }

    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) return 'No date';

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsedDate);
  };

  const toggleImageSelection = (index) => {
    setSelectedImages((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <div style={{ backgroundColor: '#f8f9fb', minHeight: '100vh' }}>
      {/* Upload Modal */}
      {files?.length > 0 && showUploadModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          style={{ backdropFilter: 'blur(4px)' }}>
          <div
            className="bg-card w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col"
            style={{
              borderRadius: '1rem',
              boxShadow:
                '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
              backgroundColor: 'white'
            }}>
            <div
              className="flex items-center justify-between p-6 border-b border-border"
              style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h2
                className="text-xl font-semibold"
                style={{ color: '#1f2937' }}>
                Upload Images ({files.length})
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setFiles({ length: 0 });
                }}
                disabled={uploadingImages}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: uploadingImages ? 'not-allowed' : 'pointer',
                  opacity: uploadingImages ? '0.5' : '1'
                }}
                onMouseEnter={(e) =>
                  !uploadingImages &&
                  (e.currentTarget.style.backgroundColor = '#f3f4f6')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = 'transparent')
                }>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">{renderUploadPreview()}</div>
            </div>

            <div
              className="flex items-center justify-end gap-3 p-6 border-t"
              style={{ borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setFiles({ length: 0 });
                }}
                disabled={uploadingImages}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: isMobileView ? '12px' : '14px',
                  fontWeight: '500',
                  cursor: uploadingImages ? 'not-allowed' : 'pointer',
                  opacity: uploadingImages ? '0.5' : '1'
                }}>
                Cancel
              </button>
              <button
                onClick={handleUploadFiles}
                disabled={uploadingImages || files.length === 0}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  backgroundColor:
                    uploadingImages || files.length === 0
                      ? '#9ca3af'
                      : '#3766a8',
                  color: 'white',
                  fontSize: isMobileView ? '12px' : '14px',
                  fontWeight: '500',
                  cursor:
                    uploadingImages || files.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                }}>
                {uploadingImages ? `Uploading... ${progress}%` : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for image uploads */}
      <input
        ref={uploadInputRef}
        style={{ visibility: 'hidden', position: 'absolute' }}
        id="image_uploads"
        onChange={(e) => {
          setFiles(e.target.files);
          setShowUploadModal(true);
        }}
        type="file"
        multiple
        accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
      />

      {/* Main Content */}
      <main
        className="overflow-x-hidden"
        style={{
          padding: isMobileView ? '0.75rem' : '2.5rem',
          paddingBottom: isMobileView ? '1rem' : '1.5rem'
        }}>
        {/* Page Title */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-2">
          <div className="flex items-center gap-4 min-w-0">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground break-words">
                Image Library, {selectedClient ? selectedClient.clientName : ''}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Manage and organize all your media files in one place
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
            {(!isWebviewMode || isWebView) && (
              <button
                onClick={() => uploadInputRef.current?.click()}
                style={{
                  backgroundColor: '#3766a8',
                  color: 'white',
                  padding: isMobileView ? '0 1.25rem' : '0 2rem',
                  borderRadius: '0.75rem',
                  boxShadow:
                    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                  border: 'none',
                  cursor: 'pointer',
                  height: isMobileView ? '48px' : '56px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  justifyContent: 'center',
                  fontSize: isMobileView ? '15px' : '18px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  width: isMobileView ? '100%' : 'auto',
                  minWidth: isMobileView ? '0' : 'auto'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2d5389';
                  e.currentTarget.style.boxShadow =
                    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3766a8';
                  e.currentTarget.style.boxShadow =
                    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
                }}>
                <Upload className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{isMobileView ? 'Upload' : 'Upload Images'}</span>
              </button>
            )}
            {isSuperAdmin && !isWebviewMode && (
              <div className="hidden lg:block relative w-72">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                <ReactSelect
                  placeholder="Search"
                  onChange={(client) => handleChangeClient(client)}
                  options={searchableCustomerList}
                  hideSelectedOptions={true}
                  controlShouldRenderValue={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null
                  }}
                  openMenuOnClick={false}
                  openMenuOnFocus={false}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      borderWidth: '1px',
                      borderColor: state.isFocused
                        ? 'hsl(var(--primary))'
                        : 'hsl(var(--border))',
                      boxShadow: state.isFocused
                        ? '0 0 0 1px hsl(var(--primary))'
                        : 'none',
                      backgroundColor: '#ffffff',
                      paddingTop: '10px',
                      paddingBottom: '9px',
                      paddingLeft: '2.5rem',
                      fontSize: '1rem',
                      '&:hover': {
                        borderColor: 'hsl(var(--primary))'
                      }
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: 'hsl(var(--muted-foreground))',
                      fontSize: '1rem'
                    }),
                    input: (provided) => ({
                      ...provided,
                      fontSize: '1rem',
                      color: 'hsl(var(--foreground))'
                    }),
                    menu: (provided) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      overflow: 'hidden',
                      zIndex: 9999,
                      boxShadow:
                        '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                      color: state.isFocused ? '#1e293b' : '#64748b',
                      cursor: 'pointer',
                      padding: '12px 16px',
                      fontSize: '1rem'
                    })
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {isSuperAdmin &&
          (customerList.length === 0 ? (
            <LoadingSpinner />
          ) : (
            <div className="flex w-full items-center justify-end">
              <div className="inline-flex items-center rounded-xl border bg-muted/50 p-1 shadow-sm">
                <div className="inline-flex items-center rounded-lg bg-background/80 p-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={
                      `h-8 px-3 text-xs sm:text-sm rounded-md transition-all ` +
                      (clientNavigationMode === 'all'
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                    onClick={() => setClientNavigationMode('all')}>
                    All Clients
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={
                      `h-8 px-3 text-xs sm:text-sm rounded-md transition-all ` +
                      (clientNavigationMode === 'no_ai'
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                    onClick={() => setClientNavigationMode('no_ai')}
                    disabled={noAIClientList.length === 0}>
                    No AI Clients ({noAIClientList.length})
                  </Button>
                </div>

                {/* <div className="mx-2 h-6 w-px bg-border" /> */}

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    disabled={
                      navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      ) === 0
                    }
                    onClick={() => {
                      let x = navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      );
                      if (x > 0) {
                        handleChangeClient(navigableCustomerList[x - 1]);
                      }
                    }}>
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <span className="text-base font-medium text-foreground min-w-[60px] text-center">
                    {navigableCustomerList.length > 0
                      ? `${
                          navigableCustomerList.findIndex(
                            (customer) => customer.id === selectedClient.id
                          ) + 1
                        }/${navigableCustomerList.length}`
                      : '0/0'}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    disabled={
                      navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      ) >=
                      navigableCustomerList.length - 1
                    }
                    onClick={() => {
                      let x = navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      );
                      if (x < navigableCustomerList.length - 1) {
                        handleChangeClient(navigableCustomerList[x + 1]);
                      }
                    }}>
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
      </main>

      {/* Navigation Menu - Sticky */}
      <div
        className="sticky top-0 z-10 overflow-x-hidden"
        style={{
          backgroundColor: '#f8f9fb',
          backdropFilter: 'blur(8px)',
          padding: isMobileView ? '0.5rem 0.75rem' : '0.75rem 2.5rem',
          marginBottom: isMobileView ? '1rem' : '2rem'
        }}>
        <div
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid #e5e7eb',
            padding: '0.375rem',
            borderRadius: '0.75rem',
            width: isMobileView ? '100%' : 'fit-content',
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
          }}>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <button
              onClick={() => handleImageSourceChange('gbp')}
              style={{
                padding: isMobileView ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '500',
                backgroundColor:
                  imageSource === 'gbp' ? '#3766a8' : 'transparent',
                color: imageSource === 'gbp' ? 'white' : '#1f2937',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (imageSource !== 'gbp')
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (imageSource !== 'gbp')
                  e.currentTarget.style.backgroundColor = 'transparent';
              }}>
              <Building2 className="h-4 w-4 shrink-0" />
              {isMobileView
                ? sourceLabels.gbp.mobile
                : sourceLabels.gbp.desktop}
              <span
                style={{
                  marginLeft: '0.25rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '9999px',
                  backgroundColor:
                    imageSource === 'gbp'
                      ? 'rgba(255,255,255,0.25)'
                      : '#e5e7eb',
                  color: imageSource === 'gbp' ? 'white' : '#4b5563',
                  fontSize: '11px',
                  fontWeight: '600',
                  height: '18px',
                  minWidth: '18px',
                  padding: '0 4px'
                }}>
                {imageStats.gbp}
              </span>
            </button>

            <button
              onClick={() => handleImageSourceChange('image-to-image')}
              style={{
                padding: isMobileView ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '500',
                backgroundColor:
                  imageSource === 'image-to-image' ? '#3766a8' : 'transparent',
                color: imageSource === 'image-to-image' ? 'white' : '#1f2937',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (imageSource !== 'image-to-image')
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (imageSource !== 'image-to-image')
                  e.currentTarget.style.backgroundColor = 'transparent';
              }}>
              <Wand2 className="h-4 w-4 shrink-0" />
              {isMobileView
                ? sourceLabels['image-to-image'].mobile
                : sourceLabels['image-to-image'].desktop}
              <span
                style={{
                  marginLeft: '0.25rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '9999px',
                  backgroundColor:
                    imageSource === 'image-to-image'
                      ? 'rgba(255,255,255,0.25)'
                      : '#e5e7eb',
                  color: imageSource === 'image-to-image' ? 'white' : '#4b5563',
                  fontSize: '11px',
                  fontWeight: '600',
                  height: '18px',
                  minWidth: '18px',
                  padding: '0 4px'
                }}>
                {imageStats.imageToImage}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Content Area with proper spacing */}
      <div
        style={{
          padding: isMobileView
            ? '0 0.75rem 5.5rem 0.75rem'
            : '0 2.5rem 2.5rem 2.5rem'
        }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobileView ? '1rem' : '2rem'
          }}>
          {/* Image Gallery Card */}
          <Card
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              border: 'none',
              boxShadow:
                '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
            }}>
            <div
              style={{
                padding: isMobileView
                  ? '1rem 1rem 0.75rem 1rem'
                  : '1.5rem 2rem 1rem 2rem'
              }}>
              <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                <div
                  style={{
                    width: isMobileView ? '42px' : '48px',
                    height: isMobileView ? '42px' : '48px',
                    borderRadius: '0.75rem',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <ImageIcon
                    width="24"
                    height="24"
                    style={{ color: '#3766a8' }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3
                    style={{
                      fontSize: isMobileView ? '18px' : '20px',
                      fontWeight: '700',
                      color: '#1a1a1a',
                      margin: 0,
                      lineHeight: '1.2',
                      fontFamily: 'inherit'
                    }}>
                    {isMobileView
                      ? sourceLabels[imageSource]?.mobileTitle
                      : sourceLabels[imageSource]?.title}
                  </h3>
                  <p
                    style={{
                      fontSize: isMobileView ? '14px' : '16px',
                      color: '#6b7280',
                      marginTop: '0.25rem',
                      marginBottom: 0,
                      lineHeight: '1.5',
                      fontFamily: 'inherit'
                    }}>
                    {imageSource === 'image-to-image'
                      ? 'AI-generated variations from your source images'
                      : isWebView
                      ? ''
                      : 'Browse and manage all images for this client'}
                  </p>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  {imageSource === 'image-to-image' && isSuperAdmin && (
                    <Button
                      size="sm"
                      className="rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-semibold gap-1.5"
                      style={{ backgroundColor: '#3766a8', color: 'white' }}
                      onClick={openGenerateModal}>
                      <Wand2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Generate
                    </Button>
                  )}
                  {(isWebView || isWebviewMode) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-medium gap-1.5"
                      disabled={galleryRefreshing}
                      onClick={handleRefreshGallery}>
                      {galleryRefreshing ? (
                        <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      )}
                      {galleryRefreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  )}
                  {imageSource === 'gbp' &&
                    isSuperAdmin &&
                    !isWebView &&
                    !isWebviewMode && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-medium gap-1.5"
                        disabled={resyncLoading}
                        onClick={async () => {
                          if (!clientId) return;
                          setResyncLoading(true);
                          try {
                            for (let i = 0; i < 2; i++) {
                              await axios.post(
                                LOAD_IMAGES_API,
                                { clientId },
                                {
                                  headers: {
                                    'Content-Type': 'application/json'
                                  }
                                }
                              );
                            }
                            Swal.fire({
                              icon: 'success',
                              title: 'Sync Complete',
                              text: 'Image synchronization has finished successfully.',
                              confirmButtonText: 'OK'
                            }).then(() => {
                              window.location.reload();
                            });
                          } catch (error) {
                            Swal.fire({
                              icon: 'error',
                              title: 'Sync Failed',
                              text: 'An error occurred while syncing images. Please try again.',
                              confirmButtonText: 'OK'
                            });
                          } finally {
                            setResyncLoading(false);
                          }
                        }}>
                        {resyncLoading ? (
                          <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        )}
                        {resyncLoading ? 'Syncing...' : 'Resync Images'}
                      </Button>
                    )}
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {images.length}
                    </span>{' '}
                    images.
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                padding: isMobileView ? '0 1rem 1rem 1rem' : '0 2rem 2rem 2rem'
              }}>
              {isLoadingData && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                  {Array.from({ length: 12 }).map((_, idx) => (
                    <div
                      key={idx}
                      className="h-32 sm:h-48 bg-gray-200 rounded-lg animate-pulse"
                      style={{ borderRadius: '0.75rem' }}
                    />
                  ))}
                </div>
              )}

              {!isLoadingData && images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                  {images.map((image, index) => {
                    const imageUrl = getImageUrl(image);
                    const isRotating = rotatingImageUrl === imageUrl;
                    const imageDateLabel = formatImageDateLabel(image);

                    return (
                      <div
                        key={imageUrl || index}
                        className="relative group overflow-hidden border-2 transition-all cursor-pointer"
                        style={{
                          borderRadius: '0.75rem',
                          borderColor: selectedImages.includes(index)
                            ? '#3766a8'
                            : 'transparent',
                          boxShadow: selectedImages.includes(index)
                            ? '0 0 0 2px rgba(55, 102, 168, 0.2)'
                            : 'none',
                          cursor:
                            imageSource === 'image-to-image'
                              ? 'default'
                              : 'pointer'
                        }}
                        onClick={() => toggleImageSelection(index)}
                        onMouseEnter={(e) => {
                          const deleteBtn = e.currentTarget.querySelector(
                            'button[data-delete-btn]'
                          );
                          if (deleteBtn) deleteBtn.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const deleteBtn = e.currentTarget.querySelector(
                            'button[data-delete-btn]'
                          );
                          if (deleteBtn) deleteBtn.style.opacity = '0';
                        }}>
                        <img
                          key={`${imageUrl}-${imageVersions[imageUrl] || 0}`}
                          src={galleryImageSrc(imageUrl)}
                          alt={getSourceLabel()}
                          className={`w-full h-28 sm:h-48 object-cover transition-transform ${
                            imageSource !== 'image-to-image'
                              ? 'group-hover:scale-105'
                              : ''
                          }`}
                          onError={(e) => {
                            e.target.src =
                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U1ZTdlYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />

                        <div className="absolute bottom-0 left-0 right-0 flex items-center gap-1 bg-foreground/75 px-1.5 py-1 sm:px-2.5 sm:py-1.5 pointer-events-none">
                          <Calendar className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 text-background shrink-0" />
                          <span className="text-[9px] sm:text-[11px] font-medium text-background leading-tight truncate">
                            {imageDateLabel}
                          </span>
                        </div>

                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          title="Rotate image"
                          aria-label={`Rotate image ${index + 1}`}
                          disabled={!imageUrl || Boolean(rotatingImageUrl)}
                          className={`absolute top-2 right-12 z-10 h-8 w-8 shadow-sm transition-opacity ${
                            isMobileView || isRotating
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
                          }`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRotateImage(image);
                          }}>
                          {isRotating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCw className="h-4 w-4" />
                          )}
                        </Button>

                        <button
                          data-delete-btn="true"
                          className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full flex items-center justify-center transition-opacity"
                          style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            opacity: isMobileView ? '1' : '0',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc2626';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(image);
                          }}>
                          <Trash2 className="h-4 w-4" />
                        </button>

                        {selectedImages.includes(index) && (
                          <div
                            className="absolute top-3 left-3 h-8 w-8 flex items-center justify-center"
                            style={{
                              backgroundColor: '#3766a8',
                              color: 'white',
                              borderRadius: '0.5rem'
                            }}>
                            <CheckCircle2 className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!isLoadingData && images.length === 0 && (
                <div className="text-center py-12">
                  <ImageIcon
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: '#9ca3af' }}
                  />
                  <h3
                    className="text-lg font-semibold mb-2"
                    style={{ color: '#1f2937' }}>
                    No images found
                  </h3>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    No images available for this client yet.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {selectedImages.length > 0 && (
          <div
            className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-[calc(100vw-1.5rem)] sm:w-auto max-w-md sm:max-w-none"
            style={{
              backgroundColor: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '1rem',
              boxShadow:
                '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
              padding: isMobileView ? '0.875rem 1rem' : '1rem 1.5rem'
            }}>
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <CheckCircle2
                style={{ width: '20px', height: '20px', color: '#3766a8' }}
              />
              <span
                style={{
                  fontSize: isMobileView ? '14px' : '16px',
                  fontWeight: '500',
                  color: '#1a1a1a'
                }}>
                <span style={{ fontWeight: '700' }}>
                  {selectedImages.length}
                </span>{' '}
                image(s) selected
              </span>
            </div>
            <div
              style={{
                height: isMobileView ? '1px' : '24px',
                width: isMobileView ? '100%' : '1px',
                backgroundColor: '#e5e7eb'
              }}
            />
            <button
              onClick={handleDeleteSelected}
              style={{
                borderRadius: '0.75rem',
                padding: '0.625rem 1rem',
                fontSize: isMobileView ? '12px' : '14px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontWeight: '600',
                height: '44px'
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = '#dc2626')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = '#ef4444')
              }>
              <Trash2 className="h-4 w-4" />
              Delete Selected
            </button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setSelectedImages([])}
              className="w-full sm:w-auto"
              style={{
                borderRadius: '0.75rem',
                height: '44px',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '600'
              }}>
              Clear Selection
            </Button>
          </div>
        )}
      </div>

      {/* Upload Error Modal */}
      <Dialog
        open={uploadErrorModalOpen}
        onOpenChange={setUploadErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Upload Error
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              {uploadErrorMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setUploadErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Single Image Confirmation Modal */}
      <Dialog
        open={deleteConfirmModalOpen}
        onOpenChange={setDeleteConfirmModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Delete Image
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Are you sure you want to delete this image?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={confirmDeleteImage}>
              OK
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmModalOpen(false);
                setImageToDelete(null);
              }}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Error Modal */}
      <Dialog
        open={deleteErrorModalOpen}
        onOpenChange={setDeleteErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Failed to delete image.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setDeleteErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Multiple Confirmation Modal */}
      <Dialog
        open={deleteMultipleConfirmModalOpen}
        onOpenChange={setDeleteMultipleConfirmModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Delete Images
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              {`Are you sure you want to delete ${selectedImages.length} image(s)?`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={confirmDeleteMultiple}>
              OK
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteMultipleConfirmModalOpen(false)}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Multiple Error Modal */}
      <Dialog
        open={deleteMultipleErrorModalOpen}
        onOpenChange={setDeleteMultipleErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Failed to delete some images.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setDeleteMultipleErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate from App Uploads Modal */}
      <Dialog
        open={generateModalOpen}
        onOpenChange={(open) => {
          setGenerateModalOpen(open);
          if (!open) {
            setGenerateSelectedImages([]);
            setGenerateResults(null);
            setGenerateResultsView(false);
          }
        }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden !top-[50%] !translate-y-[-50%] p-0">
          {/* ── RESULTS VIEW ── */}
          {generateResultsView && generateResults ? (
            <>
              <div className="px-6 pt-6 pb-3">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
                    <Wand2 className="h-6 w-6 text-primary" />
                    Generated Alternatives
                  </DialogTitle>
                  <DialogDescription className="text-base text-muted-foreground mt-1">
                    {generateResults.totalGenerated} image(s) generated from{' '}
                    {generateResults.inputImages} source(s) · model:{' '}
                    <span className="font-medium text-foreground">
                      {generateResults.model}
                    </span>
                  </DialogDescription>
                </DialogHeader>
              </div>

              <div
                className="px-6 pb-6 overflow-y-auto"
                style={{ maxHeight: '55vh' }}>
                {Object.entries(generateResults.grouped).map(
                  ([originalUrl, variants]) => (
                    <div key={originalUrl} className="mb-6">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={originalUrl}
                          alt="Original"
                          className="h-12 w-12 rounded-lg object-cover border border-border"
                        />
                        <p
                          className="text-xs text-muted-foreground truncate"
                          style={{ maxWidth: '80%' }}>
                          {originalUrl}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[...variants]
                          .sort((a, b) =>
                            String(a.promptSk).localeCompare(String(b.promptSk))
                          )
                          .map((variant) => (
                            <div
                              key={variant.url}
                              className="rounded-xl overflow-hidden border border-border">
                              <img
                                src={variant.url}
                                alt={variant.promptType}
                                className="w-full h-36 object-cover"
                              />
                              <div className="p-2">
                                <p className="text-xs font-semibold text-foreground mb-1 truncate">
                                  {variant.promptType}
                                </p>
                                <div className="flex gap-1">
                                  <a
                                    href={variant.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-primary underline">
                                    Open
                                  </a>
                                  <span className="text-muted-foreground text-xs">
                                    ·
                                  </span>
                                  <button
                                    className="text-xs text-primary underline"
                                    onClick={() =>
                                      navigator.clipboard.writeText(variant.url)
                                    }>
                                    Copy URL
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )
                )}
              </div>

              <div
                className="z-20 border-t px-6 py-4 flex justify-between gap-3"
                style={{
                  backgroundColor: '#ffffff',
                  boxShadow: '0 -8px 16px rgba(15, 23, 42, 0.08)'
                }}>
                <Button
                  variant="outline"
                  className="px-6 h-11 text-base font-semibold rounded-lg"
                  onClick={() => {
                    setGenerateResultsView(false);
                    setGenerateResults(null);
                    setGenerateSelectedImages([]);
                  }}>
                  Generate More
                </Button>
                <Button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 h-11 text-base font-semibold rounded-lg"
                  onClick={() => {
                    setGenerateModalOpen(false);
                    setGenerateResultsView(false);
                    setGenerateResults(null);
                    setGenerateSelectedImages([]);
                  }}>
                  Done
                </Button>
              </div>
            </>
          ) : (
            /* ── SELECTION VIEW ── */
            <>
              <div className="px-6 pt-6">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
                    <Wand2 className="h-6 w-6 text-primary" />
                    Select Source Images,,,,,
                  </DialogTitle>
                  <DialogDescription className="text-base text-muted-foreground mt-1">
                    Choose one or more images from this client's image library
                    to generate AI variations.
                  </DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {generateSelectedImages.length}
                    </span>{' '}
                    of {generateAppImages.length} selected
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg h-9 text-sm font-medium"
                    onClick={handleToggleSelectAllGenerate}
                    disabled={
                      generateLoading || generateAppImages.length === 0
                    }>
                    {generateSelectedImages.length ===
                      generateAppImages.length && generateAppImages.length > 0
                      ? 'Clear All'
                      : 'Select All'}
                  </Button>
                </div>
              </div>

              <div
                className="px-6 pb-6 overflow-y-auto"
                style={{ maxHeight: '50vh' }}>
                {generateLoading && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <div
                        key={idx}
                        className="h-32 sm:h-40 bg-gray-200 rounded-lg animate-pulse"
                      />
                    ))}
                  </div>
                )}

                {!generateLoading && generateAppImages.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pb-4">
                    {generateAppImages.map((image, index) => {
                      const imageUrl = image.sourceUrl || image.SK;
                      const isSelected =
                        generateSelectedImages.includes(imageUrl);

                      return (
                        <div
                          key={imageUrl || index}
                          onClick={() => toggleGenerateImageSelection(imageUrl)}
                          className={`relative group rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-transparent hover:border-muted-foreground/30'
                          }`}>
                          <img
                            key={`${imageUrl}-${imageVersions[imageUrl] || 0}`}
                            src={galleryImageSrc(imageUrl)}
                            alt={`Customer upload ${index + 1}`}
                            className="w-full h-32 sm:h-40 object-cover"
                          />
                          {isSelected && (
                            <div className="absolute top-2 left-2 h-7 w-7 bg-primary text-primary-foreground rounded-lg flex items-center justify-center">
                              <CheckCircle2 className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {!generateLoading && generateAppImages.length === 0 && (
                  <div className="text-center py-10">
                    <ImageIcon
                      className="h-14 w-14 mx-auto mb-3"
                      style={{ color: '#9ca3af' }}
                    />
                    <p className="text-sm text-muted-foreground">
                      No app uploads found for this client.
                    </p>
                  </div>
                )}
              </div>

              <div
                className="z-20 border-t px-6 py-4 flex justify-end gap-3"
                style={{
                  backgroundColor: '#ffffff',
                  boxShadow: '0 -8px 16px rgba(15, 23, 42, 0.08)'
                }}>
                <Button
                  variant="outline"
                  className="px-6 h-11 text-base font-semibold rounded-lg"
                  onClick={() => setGenerateModalOpen(false)}
                  disabled={generateSubmitting}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    generateSubmitting || selectedGeneratePayload.length === 0
                  }
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 h-11 text-base font-semibold rounded-lg gap-2"
                  onClick={handleGenerateFromSelected}>
                  {generateSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4" />
                  )}
                  {generateSubmitting
                    ? 'Generating...'
                    : `Generate (${selectedGeneratePayload.length})`}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Images;
